import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  GetNewsData,
  GetNewsKeyword,
  getNewsThumbnail,
} from "../Api/callData.js";

/* ──────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
`;

/* ── Hero ── */
const Hero = styled.section`
  display: block;
  min-height: auto;
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(48px, 6vw, 72px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(40px, 5.8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const HeroLead = styled.p`
  font-size: clamp(16px, 1.5vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto 36px;
  max-width: 620px;
`;

const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
`;

const Tab = styled.a`
  padding: 9px 22px;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 500;
  text-decoration: none;
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "transparent"};
  box-shadow: ${(p) =>
    p.$active ? "0 6px 14px -6px rgba(99, 102, 241, 0.55)" : "none"};
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
`;

/* ── Content area ── */
const ContentSection = styled.section`
  display: block;
  min-height: auto;
  padding: clamp(40px, 5vw, 64px) 0 clamp(80px, 10vw, 120px);
`;

/* ── Search ── */
const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 460px;
  margin: 0 0 40px;
  padding: 6px 8px 6px 18px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #c7d2fe;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.08);
  }

  input {
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 15px;
    color: #0f172a;
    font-family: inherit;
  }
  input::placeholder {
    color: #94a3b8;
  }

  button {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: 0;
    background: #0f172a;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  button:hover {
    background: #1e293b;
  }
`;

/* ── Card grid ── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.a`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #c7d2fe;
    transform: translateY(-3px);
    box-shadow: 0 18px 35px -18px rgba(15, 23, 42, 0.15);
  }

  .cover {
    aspect-ratio: 16 / 10;
    width: 100%;
    object-fit: cover;
    background: #f1f5f9;
    display: block;
  }
  .body {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
  }
  .title {
    font-size: 17px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.4;
    letter-spacing: -0.01em;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .date {
    font-size: 13px;
    color: #94a3b8;
    margin-top: auto;
  }
`;

/* ── Pagination ── */
const Pagination = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 56px;
`;

const PageBtn = styled.button`
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "#e2e8f0")};
  border-radius: 8px;
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "#ffffff"};
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.$active ? "transparent" : "#cbd5e1")};
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ── Loading + empty states ── */
const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 15px;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

/* ── Admin write button ── */
const AdminBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 18px;
    border-radius: 999px;
    background: #0f172a;
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: background 0.15s ease;
  }
  a:hover {
    background: #1e293b;
  }
`;

/* ── Icons ── */
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);
const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);
const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 18l6-6-6-6" />
  </svg>
);
const ChevronsLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 18l-6-6 6-6M19 18l-6-6 6-6" />
  </svg>
);
const ChevronsRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M13 18l6-6-6-6M5 18l6-6-6-6" />
  </svg>
);

const PAGE_SIZE = 9;

export function News() {
  const [post, setPost] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [nextFirst, setNextFirst] = useState(0);

  const [keyword, setKeyword] = useState("");
  const [keywordInput, setKeywordInput] = useState("");

  const submitSearch = () => setKeyword(keywordInput.trim());
  const onKeyDown = (e) => {
    if (e.key === "Enter") submitSearch();
  };

  useEffect(() => {
    if (keyword === "") {
      const load = async () => {
        setLoading(true);
        const { postsData, lastVisibleDoc, querySnapShotAll, lastPost } =
          await GetNewsData(currentPage, lastVisible, nextFirst);
        setPost(postsData);
        setLastVisible(lastVisibleDoc);
        setLoading(false);
        setTotalPosts(querySnapShotAll);
        setNextFirst(lastPost);
      };
      load();
    } else {
      const load = async () => {
        setLoading(true);
        const { postsResData, lastResVisibleDoc, allUniqueDocs, lastResPost } =
          await GetNewsKeyword(keyword, currentPage, lastVisible, nextFirst);
        setPost(postsResData);
        setLastVisible(lastResVisibleDoc);
        setLoading(false);
        setTotalPosts(allUniqueDocs);
        setNextFirst(lastResPost);
      };
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, currentPage]);

  const totalPages = Math.max(1, Math.ceil((totalPosts || 0) / PAGE_SIZE));

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const isLoggedIn = typeof window !== "undefined" && localStorage.length > 0;

  return (
    <Page>
      <Hero>
        <Container>
          <Eyebrow>Community</Eyebrow>
          <HeroTitle>News</HeroTitle>
          <HeroLead>
            What&apos;s happening at Youngand — press coverage, milestones, and
            announcements from our team and partners.
          </HeroLead>
          <Tabs>
            <Tab href="/community/inform">Inform</Tab>
            <Tab href="/community/news" $active>
              News
            </Tab>
            <Tab href="/community/hiring">Recruitment</Tab>
          </Tabs>
        </Container>
      </Hero>

      <ContentSection>
        <Container>
          <SearchBar>
            <input
              type="text"
              placeholder="Search news"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={onKeyDown}
            />
            <button type="button" onClick={submitSearch} aria-label="Search">
              <SearchIcon />
            </button>
          </SearchBar>

          {isLoggedIn && (
            <AdminBar>
              <a href="/adminWrite">Write a post</a>
            </AdminBar>
          )}

          {loading ? (
            <StateBox>
              <Spinner />
              <div>Loading news…</div>
            </StateBox>
          ) : post && post.length > 0 ? (
            <>
              <Grid>
                {post.map((p) => (
                  <Card key={p.id} href={`/community/news/${p.id}`}>
                    <img
                      className="cover"
                      src={getNewsThumbnail(p)}
                      loading="lazy"
                      alt=""
                    />
                    <div className="body">
                      <div className="title">{p.title}</div>
                      <div className="date">
                        {p.reg_time
                          ? p.reg_time.toDate().toISOString().split("T")[0]
                          : ""}
                      </div>
                    </div>
                  </Card>
                ))}
              </Grid>

              <Pagination aria-label="Pagination">
                <PageBtn
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  aria-label="First page"
                >
                  <ChevronsLeft />
                </PageBtn>
                <PageBtn
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  aria-label="Previous page"
                >
                  <ChevronLeft />
                </PageBtn>
                {[...Array(totalPages)].map((_, idx) => (
                  <PageBtn
                    key={idx}
                    $active={currentPage === idx + 1}
                    onClick={() => handlePageChange(idx + 1)}
                  >
                    {idx + 1}
                  </PageBtn>
                ))}
                <PageBtn
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  aria-label="Next page"
                >
                  <ChevronRight />
                </PageBtn>
                <PageBtn
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || loading}
                  aria-label="Last page"
                >
                  <ChevronsRight />
                </PageBtn>
              </Pagination>
            </>
          ) : (
            <StateBox>
              <div style={{ fontSize: 36, lineHeight: 1 }}>📰</div>
              <div>No news posts yet.</div>
              {keyword && (
                <div style={{ fontSize: 13, color: "#cbd5e1" }}>
                  Try a different search term.
                </div>
              )}
            </StateBox>
          )}
        </Container>
      </ContentSection>
    </Page>
  );
}
